import React, { useState } from "react";
import api from "../lib/api";
import { Calculator, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB", "ADA", "DOGE", "AVAX", "DOT", "HYPE"];

export default function ProfitCalculator() {
  const [asset, setAsset] = useState("BTC");
  const [amount, setAmount] = useState("1000");
  const [date, setDate] = useState("2020-01-01");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/tools/profit-calculator", {
        params: { asset, amount_usd: amt, buy_date: date },
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not calculate");
    } finally { setLoading(false); }
  };

  const isProfit = result && result.profit_usd >= 0;

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Calculator className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Crypto Profit Calculator</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          See what your investment would be worth today if you had bought any date in the past.
        </p>
      </div>

      <form onSubmit={calculate} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Coin</label>
            <select value={asset} onChange={(e) => setAsset(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
              {ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Investment $</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Buy Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="mt-4 w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
          Calculate
        </button>
      </form>

      {result && (
        <div className={"mt-6 border p-6 " + (isProfit ? "border-green-400/30 bg-green-400/5" : "border-red-400/30 bg-red-400/5")}>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            If you invested <span className="text-[var(--text-primary)] font-bold">${result.amount_usd.toLocaleString()}</span> in{" "}
            <span className="text-[var(--text-primary)] font-bold">{result.asset}</span> on{" "}
            <span className="text-[var(--text-primary)] font-bold">{result.buy_date}</span> at ${result.historical_price.toLocaleString()}, here's where you'd stand today:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Investment</div>
              <div className="font-display font-bold text-2xl text-[var(--text-primary)]">${result.amount_usd.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Current Value</div>
              <div className="font-display font-bold text-2xl text-[var(--text-primary)]">${result.current_value.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Profit</div>
              <div className={"font-display font-bold text-2xl flex items-center gap-1 " + (isProfit ? "text-green-400" : "text-red-400")}>
                {isProfit ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {isProfit ? "+" : ""}{result.profit_pct}%
              </div>
            </div>
          </div>

          <p className="text-xs font-mono text-[var(--text-tertiary)]">
            {result.coins_bought} {result.asset} bought at ${result.historical_price.toLocaleString()}, now worth ${result.current_price.toLocaleString()} each.
          </p>
        </div>
      )}

      <p className="mt-8 text-xs text-[var(--text-tertiary)] text-center">
        Prices from Binance historical daily data. Past performance does not guarantee future results. Not financial advice.
      </p>


      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How this calculator works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Pick a date</div>
            <p className="text-xs text-[var(--text-secondary)]">Choose any coin, the amount you'd have invested, and the date you would have bought.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Historical price lookup</div>
            <p className="text-xs text-[var(--text-secondary)]">The calculator pulls the coin's actual market price on that exact date to work out how many coins your investment would have bought.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Compare to today</div>
            <p className="text-xs text-[var(--text-secondary)]">Those coins are then valued at today's live price, showing the current value, total profit, and percentage return.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          This tool is useful for understanding what past investments would be worth today, comparing how different coins performed from the same starting point, or simply satisfying curiosity about a missed opportunity. Try the same amount and date across a few coins to see how differently they've performed.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          Results use closing market price on the selected date and the current live price, both from CoinGecko. This calculation does not account for exchange fees, taxes, or the practical difficulty of buying at an exact historical price. It is a simplified illustration of price performance, not investment advice.
        </p>
      </div>
    </div>
  );
}